/**
 * The case file as the workspace hosts it (kanban 445962e2): the "📁 Case
 * file" head with its ⤢ pop-out, the canonical InvestigationCaseFile under
 * it, and the "Asks on this case" list that follows. The thread workspace
 * (ThreadDetailPane) and the case-only workspace (QualityWorkspacePage)
 * used to each build this block by hand; one component keeps them one.
 */
import type { ReactNode } from 'react';

import { InvestigationCaseFile } from '../../components/portal/InvestigationCaseFile';
import { CASE_FILE_POPOUT_HINT, CASE_FILE_POPOUT_LABEL } from '../../components/portal/caseFileExpand';
import { AsksSection } from '../../components/quality/AsksSection';
import type { DoorDataRequestRead } from '../../services/qcpDataRequests';
import type { DoorCaseTaskRead, DoorInvestigationRead } from '../../services/qcpInvestigations';
import styles from './QualityWorkspace.module.css';

export interface CaseFileSectionProps {
  investigation: DoorInvestigationRead;
  onUpdated: (updated: DoorInvestigationRead) => void;
  /** Open the pop-out modal (the host owns it, since it covers the page). */
  onPopOut: () => void;
  /** Said once in the head when this pane just minted the case, so "Open
   *  investigation" visibly lands somewhere. */
  note?: ReactNode;
  /** The thread workspace prints the title and numbers in its own head. */
  embedded?: boolean;
  openAskCount?: number;
  activeConversationId?: string | null;
  onOpenLinkedConversation?: (conversationId: string) => void;
  asks?: ReadonlyArray<DoorDataRequestRead>;
  onAskTask?: (task: DoorCaseTaskRead) => void;
  taskDesks?: Record<string, string>;
}

export function CaseFileSection({
  investigation,
  onUpdated,
  onPopOut,
  note,
  embedded = false,
  openAskCount,
  activeConversationId,
  onOpenLinkedConversation,
  asks,
  onAskTask,
  taskDesks,
}: CaseFileSectionProps) {
  return (
    <section className={styles.caseSection} aria-label={`Case file ${investigation.investigation_no}`}>
      <div className={styles.caseHead}>
        📁 Case file
        {note && (
          <span className={styles.caseOpenedNote} role="status">
            {note}
          </span>
        )}
        <button
          type="button"
          className={styles.caseExpandBtn}
          onClick={onPopOut}
          aria-label={CASE_FILE_POPOUT_LABEL}
          title={CASE_FILE_POPOUT_HINT}
        >
          <span aria-hidden="true">⤢</span>
        </button>
      </div>
      <InvestigationCaseFile
        key={investigation.id}
        investigation={investigation}
        onUpdated={onUpdated}
        embedded={embedded}
        openAskCount={openAskCount}
        activeConversationId={activeConversationId}
        onOpenLinkedConversation={onOpenLinkedConversation}
        asks={asks}
        onAskTask={onAskTask}
        taskDesks={taskDesks}
      />
    </section>
  );
}

/** "Asks on this case / thread", under the case file; `#case-asks` is where
 *  the Cases tab's "Waiting on people" rows and the ask status card land. */
export function CaseAsksHost({
  title,
  requests,
  onRequestUpdated,
  hideDraftIds,
  trackContext,
}: {
  title: string;
  requests: DoorDataRequestRead[];
  onRequestUpdated: (updated: DoorDataRequestRead) => void;
  hideDraftIds?: ReadonlySet<string>;
  /** thread_no / investigation_no for the recorder's targets (kanban 529c0851). */
  trackContext?: Record<string, string | number | null | undefined>;
}) {
  if (requests.length === 0) return null;
  return (
    <div className={styles.asksHost} id="case-asks">
      <AsksSection
        title={title}
        requests={requests}
        onRequestUpdated={onRequestUpdated}
        hideDraftIds={hideDraftIds}
        trackContext={trackContext}
      />
    </div>
  );
}

export default CaseFileSection;
